/**
 * Courbe résiduelle (alignée sur analytics/residual-value.js).
 * Utilisé en secours si l’API residual-curve n’est pas disponible (réponse HTML / ancien backend).
 */

export const MIN_REL_RESIDUAL_DROP_PER_YEAR = 0.0025
const MIN_REL_RESIDUAL_DROP_PER_YEAR_SPARSE = 0.009
const SPARSE_BLEND_SYNTHETIC = 0.55
const SPARSE_COMPOUND_ANNUAL_FALLBACK = 0.068
const SPARSE_IMPLIED_MIN_ANNUAL_DROP = 0.04

function inferMotoCategory(model) {
  const m = String(model || '').toLowerCase()
  if (/scoot|xmax|tmax/.test(m)) return 'Scooter'
  if (/tracer|tenere|adventure|africa|gs|vstrom/.test(m)) return 'Trail'
  if (/cbr|r1|r6|zx|panigale|fireblade/.test(m)) return 'Sport'
  if (/tour|goldwing|rt/.test(m)) return 'Touring'
  return 'Roadster'
}

export function slopesFromPrefsFallback(model, vehiclePrice, fallbackDepreciationTable) {
  const row = Array.isArray(fallbackDepreciationTable)
    ? fallbackDepreciationTable.find((r) => String(r?.category || '').toLowerCase() === inferMotoCategory(model).toLowerCase())
    : null
  const depKm = Number(row?.coef_km || 0) * 1000
  const depYear = Number(row?.coef_year || 0) * Number(vehiclePrice || 0)
  return { depKm, depYear }
}

function syntheticResidualAtDuration(v0, km, durationMonths, annualKm, depKm, depYear, floorVal) {
  const yearsAhead = durationMonths / 12
  const projectedKm = Math.round(Number(km) + yearsAhead * Number(annualKm))
  const slope = v0 + (Number(depKm) / 1000) * (projectedKm - Number(km)) + Number(depYear) * yearsAhead
  const slopeRounded = Math.round(slope)
  const compound = Math.round(v0 * Math.pow(1 - SPARSE_COMPOUND_ANNUAL_FALLBACK, yearsAhead))
  const impliedAnnual =
    v0 > 0 && yearsAhead > 0 ? (v0 - slopeRounded) / v0 / yearsAhead : SPARSE_IMPLIED_MIN_ANNUAL_DROP
  if (impliedAnnual < SPARSE_IMPLIED_MIN_ANNUAL_DROP)
    return Math.max(
      floorVal,
      Math.round((1 - SPARSE_BLEND_SYNTHETIC) * slopeRounded + SPARSE_BLEND_SYNTHETIC * compound)
    )
  return Math.max(floorVal, slopeRounded)
}

function isSparseResidualRows(residualApiRows) {
  const ok = residualApiRows.filter((r) => r && !r.error && Number.isFinite(Number(r?.residualValue)))
  if (ok.length === 0) return false
  if (ok.every((r) => {
    const c = String(r.confidence || '').toLowerCase()
    return c === 'faible' || c === 'low'
  }))
    return true
  if (ok.filter((r) => Number.isFinite(Number(r.liquidityScore)) && Number(r.liquidityScore) <= 2.5).length >= ok.length)
    return true
  if (ok.filter((r) => Number(r.marketSampleSize) < 8).length >= Math.ceil(ok.length * 0.75)) return true
  return false
}

export function chfDepreciationBetweenMonths(prevMonths, nextMonths, annualKm, depKm, depYear) {
  const deltaYears = (nextMonths - prevMonths) / 12
  const deltaKm = deltaYears * Number(annualKm)
  return (Number(depKm) / 1000) * deltaKm + Number(depYear) * deltaYears
}

export function applySequentialResidualCurve(sortedDurations, rawResidualValues, { annualKm, depKm, depYear, floorVal, minRelPerYear }) {
  const out = []
  let prev = null
  let prevD = null
  for (let i = 0; i < sortedDurations.length; i++) {
    const raw = rawResidualValues[i]
    if (raw == null || !Number.isFinite(raw)) {
      out.push(null)
      prev = null
      prevD = null
      continue
    }
    let val = Math.round(raw)
    val = Math.max(floorVal, val)
    const d = sortedDurations[i]
    if (prev == null) {
      out.push(val)
      prev = val
      prevD = d
      continue
    }
    const fromSlope = chfDepreciationBetweenMonths(prevD, d, annualKm, depKm, depYear)
    const minRel = Math.round(prev * minRelPerYear * ((d - prevD) / 12))
    const drop = Math.max(1, Math.round(fromSlope), minRel)
    const ceiling = prev - drop
    val = Math.max(floorVal, Math.min(val, ceiling))
    out.push(val)
    prev = val
    prevD = d
  }
  return out
}

/**
 * @param {number[]} durations ex. [24,36,48,60]
 * @param {object[]} residualApiRows même ordre — réponses JSON de /api/leasing/residual
 */
export function applyCurveToResidualEntries(durations, residualApiRows, { annualKm, vehiclePrice, depKm = 0, depYear = 0, km = 0 }) {
  const floorVal = Math.max(1, Math.round(Number(vehiclePrice) * 0.15))
  const v0 = Number(vehiclePrice)
  const sparse = Number.isFinite(v0) && v0 > 0 && isSparseResidualRows(residualApiRows)

  const rawNums = residualApiRows.map((r) =>
    r?.error || !Number.isFinite(Number(r?.residualValue)) ? null : Number(r.residualValue)
  )
  const blendedNums = durations.map((d, i) => {
    const n = rawNums[i]
    if (n == null || !sparse) return n
    const syn = syntheticResidualAtDuration(v0, km, d, annualKm, depKm, depYear, floorVal)
    return Math.round((1 - SPARSE_BLEND_SYNTHETIC) * n + SPARSE_BLEND_SYNTHETIC * syn)
  })

  const adjusted = applySequentialResidualCurve(durations, blendedNums, {
    annualKm,
    depKm,
    depYear,
    floorVal,
    minRelPerYear: sparse ? MIN_REL_RESIDUAL_DROP_PER_YEAR_SPARSE : MIN_REL_RESIDUAL_DROP_PER_YEAR,
  })
  return durations.map((d, i) => {
    const raw = residualApiRows[i]
    const adj = adjusted[i]
    if (adj == null) return { durationMonths: d, ...raw }
    const rawVal = Number(raw?.residualValue)
    const blendedVal = blendedNums[i]
    return {
      durationMonths: d,
      ...raw,
      residualValue: adj,
      rawResidualValue: Number.isFinite(rawVal) ? Math.round(rawVal) : undefined,
      sparseDataModel: sparse || undefined,
      blendedBeforeCurve: sparse && Number.isFinite(blendedVal) ? Math.round(blendedVal) : undefined,
      curveAdjusted:
        (Number.isFinite(rawVal) && Math.round(rawVal) !== adj) ||
        Boolean(sparse && Number.isFinite(blendedVal) && Math.round(blendedVal) !== adj),
    }
  })
}

/** Parse fetch body : évite JSON.parse sur du HTML (SPA 404, nginx, etc.). */
export function parseJsonBodySafe(text) {
  const t = String(text || '').trim()
  if (!t || t[0] === '<') return { ok: false, data: null }
  if (t[0] !== '{' && t[0] !== '[') return { ok: false, data: null }
  try {
    return { ok: true, data: JSON.parse(t) }
  } catch {
    return { ok: false, data: null }
  }
}
